type ProcessEnvLike = {
  env?: Record<string, string | undefined>;
};

function readProcessEnv(key: string) {
  const processLike = (globalThis as { process?: ProcessEnvLike }).process;
  const value = processLike?.env?.[key];
  return typeof value === "string" ? value.trim() : "";
}

function readCsvEnv(key: string) {
  return readProcessEnv(key)
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);
}

function readConfiguredRootDomains() {
  const rootDomains = readCsvEnv("PUBLIC_ROOT_DOMAINS");
  if (rootDomains.length > 0) {
    return rootDomains;
  }
  const rootDomain = readProcessEnv("PUBLIC_ROOT_DOMAIN") || readProcessEnv("AIRANGE_ROOT_DOMAIN");
  return rootDomain ? [rootDomain] : [];
}

export function getIAiViteAllowedHosts(subdomains: Array<string | null | undefined> = []) {
  const hosts = new Set<string>();
  for (const rootDomain of readConfiguredRootDomains()) {
    hosts.add(rootDomain);
    hosts.add(`.${rootDomain}`);
    for (const subdomain of subdomains) {
      const normalized = String(subdomain || "").trim();
      if (normalized) {
        hosts.add(`${normalized}.${rootDomain}`);
      }
    }
  }
  return [...hosts];
}

function requirePrimaryRootDomain(consumerLabel: string) {
  const primaryRootDomain = readProcessEnv("PUBLIC_ROOT_DOMAIN") || readProcessEnv("AIRANGE_ROOT_DOMAIN");
  if (primaryRootDomain) {
    return primaryRootDomain;
  }
  const rootDomains = readCsvEnv("PUBLIC_ROOT_DOMAINS");
  if (rootDomains.length === 1) {
    return rootDomains[0] as string;
  }
  if (rootDomains.length > 1) {
    throw new Error(
      `${consumerLabel} requires PUBLIC_ROOT_DOMAIN or AIRANGE_ROOT_DOMAIN when PUBLIC_ROOT_DOMAINS contains multiple root domains.`,
    );
  }
  throw new Error(
    `${consumerLabel} requires PUBLIC_ROOT_DOMAIN, PUBLIC_ROOT_DOMAINS, or AIRANGE_ROOT_DOMAIN.`,
  );
}

export function requireIAiViteHmrHost(subdomain: string | null | undefined) {
  const rootDomain = requirePrimaryRootDomain("Vite HMR host");
  const normalizedSubdomain = String(subdomain || "").trim();
  return normalizedSubdomain ? `${normalizedSubdomain}.${rootDomain}` : rootDomain;
}

export type IAiViteConfigEnv = {
  command?: string;
  mode?: string;
};

export type IAiViteHmrOptions = {
  host: string;
  protocol: "wss";
  clientPort: 443;
};

export function getIAiViteHmrOptions(
  subdomain: string | null | undefined,
  configEnv: IAiViteConfigEnv,
): IAiViteHmrOptions | undefined {
  const command = String(configEnv.command || "").trim();
  const mode = String(configEnv.mode || "").trim();
  const isTestConfigLoad = mode === "test" || Boolean(readProcessEnv("VITEST"));
  if (command !== "serve" || isTestConfigLoad) {
    return undefined;
  }
  return {
    host: requireIAiViteHmrHost(subdomain),
    protocol: "wss",
    clientPort: 443,
  };
}
